import { Location, NgIf, NgFor, AsyncPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { createCmdFactory } from '@cevi/rxjscommands';
import { Observable, Subject, zip } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { AppRoutingService } from 'src/app/shared/app-routing.service';
import { INieuweNotitie, INotitie, IPartij, IPersoon, IReageerNotitie, NotitiesService } from '../notities.service';
import { FormsModule } from '@angular/forms';

interface IReageerNotitieVm {
  zittingId: number;
  partijen: IPartij[];
  notitie: INotitie;
  reactie: string;
  isEdit: boolean;
  allenSelected: boolean;
}

const dataEditkey = 'edit';

@Component({
    selector: 'app-notitie-reageer',
    templateUrl: './notitie-reageer.component.html',
    styleUrls: ['./notitie-reageer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [NgIf, FormsModule, NgFor, AsyncPipe, DatePipe],
    providers:[NotitiesService]
})
export class NotitieReageerComponent implements OnInit {

  vm$: Observable<IReageerNotitieVm>;
  vm: IReageerNotitieVm;

  constructor(private route: ActivatedRoute, private router: Router, private location: Location,
    private notitieService: NotitiesService, private routingService: AppRoutingService) {
    this.routingService.createCurrentHistoryItem('Wijzig notitie');

    const cmdFactory = createCmdFactory<IReageerNotitieVm>();
    cmdFactory.addMapAction(this.viewDataAction$, 'viewData');
    cmdFactory.addMapAction(this.toggleAllenAction$, 'toggleAllen');
    cmdFactory.addMapAction(this.togglePartijAction$, 'togglePartij');
    cmdFactory.addMapAction(this.togglePersoonAction$, 'togglePersoon');
    cmdFactory.addMapAction(this.saveAction$, 'save');

    this.vm$ = cmdFactory.merge({
      zittingId: 0,
      partijen: [],
      notitie: {},
      reactie: '',
      isEdit: false,
      allenSelected: false
    } as IReageerNotitieVm);

  }


  private params$ = this.route.paramMap.pipe(
    map(paramMap => ({
      isEdit: this.route.snapshot.data[dataEditkey] === 'edit',
      agendapuntId: +paramMap.get('agendapuntId'),
      notitieId: +paramMap.get('notitieId'),
      zittingId: +paramMap.get('zittingId')
    }))
  );

  private actieveLedenPerPartij$ = this.params$.pipe(
    mergeMap(({ agendapuntId }) => this.notitieService.getActieveLedenPerPartij(agendapuntId))
  );

  private notitie$ = this.params$.pipe(
    mergeMap(({ notitieId }) => this.notitieService.reageerNotitie(notitieId))
  );

  // ??? forkJoin werkt niet, één of beide niet completed?
  private viewDataAction$ = zip(this.actieveLedenPerPartij$, this.notitie$, this.params$).pipe(
    map(([actieveLeden, reageerNotitie, params]) => ({ actieveLeden, reageerNotitie, params })),
    map((data) => (vm: IReageerNotitieVm) => this.mapVm(vm, data.actieveLeden, data.reageerNotitie, data.params))
  );

  private mapVm = (vm: IReageerNotitieVm, partijen: IPartij[], reageerNotitie: IReageerNotitie, params) => {
    const newVm = { ...vm, partijen, notitie: reageerNotitie.Notitie, reactie: params.isEdit ? reageerNotitie.Notitie.Inhoud : '', ...params };
    newVm.partijen.forEach(partij => {
      partij.Leden.forEach(persoon =>
        persoon.isSelected = reageerNotitie.NotitieToegangen.some(id => id === persoon.Id)
      );
    });
    this.checkPartijen2(newVm);
    return newVm;
  }

  private checkPartijen2(vm: IReageerNotitieVm) {
    vm.partijen.forEach(partij => {
      partij.isSelected = partij.Leden.reduce((prev, curr) => prev && curr.isSelected, true);
    });
    this.checkAllen2(vm);
  }

  private checkAllen2(vm: IReageerNotitieVm) {
    vm.allenSelected = vm.partijen.reduce((prev, curr) => prev && curr.isSelected, true);
  }

  public toggleAllenSubj = new Subject<boolean>();
  private toggleAllenAction$ = this.toggleAllenSubj.pipe(
    map(isSelected => (vm: IReageerNotitieVm) => {
      const partijen = vm.partijen.map(partij => ({
        ...partij,
        isSelected,
        Leden: partij.Leden.map(lid => ({ ...lid, isSelected:isSelected || !lid.canSelect }))
      }));
      return { ...vm, allenSelected: isSelected, partijen };
    })
  );

  public togglePartijSubj = new Subject<IPartij>();
  private togglePartijAction$ = this.togglePartijSubj.pipe(
    map(partij => (vm: IReageerNotitieVm) => {
      partij.isSelected = !partij.isSelected;
      partij.Leden.forEach(persoon => {
        persoon.isSelected = partij.isSelected || !persoon.canSelect;
      });
      this.checkAllen2(vm);
      return vm;
    })
  );

  public togglePersoonSubj = new Subject<IPersoon>();
  private togglePersoonAction$ = this.togglePersoonSubj.pipe(
    map(persoon => (vm: IReageerNotitieVm) => {
      persoon.isSelected = !persoon.isSelected;
      this.checkPartijen2(vm);
      return vm;
    })
  );

  public saveSubj = new Subject<IReageerNotitieVm>();
  private saveAction$ = this.saveSubj.pipe(
    mergeMap(vm => {
      const notitie: INieuweNotitie = {
        Id: vm.notitie.Id,
        NotitieContent: vm.reactie,
        PersoonIds: [],
        AgendapuntId: vm.notitie.AgendapuntId
      };
      const selected = vm.partijen.map(partij => partij.Leden.filter(p => p.isSelected).map(p => p.Id));
      notitie.PersoonIds = selected.reduce((prev, curr) => { prev.push(...curr); return prev; }, notitie.PersoonIds);
      return vm.isEdit ? this.notitieService.updateNotitie(notitie) : this.notitieService.toevoegenNotitie(notitie);
    }),
    map(result => (vm: IReageerNotitieVm) => {
      this.routingService.navigateBack();
      return vm;
    })
  );

  getPartijId = (_: number, partij: IPartij) => partij.Id;
  getLidId = (_: number, lid: IPersoon) => lid.Id;

  ngOnInit() {
  }

  cancel() {
    this.routingService.navigateBack();
  }

}
